import React, { useMemo } from "react";
import { fmt, formatDMS, radToDeg } from "../lib/api";

/**
 * Plan view of the Leica TS12. Uses the real top-down photograph of the
 * instrument (bundled in /public/ts12-plan.png), rotated by the current
 * Hz value so the surveyor sees the objective always pointing towards the
 * live bearing. A compass rose is drawn behind, and a small side-elevation
 * gauge on the right shows the current zenith / V angle.
 *
 * The photo's natural orientation puts the objective on the LEFT side of
 * the image. We rotate the <img> by (hz + PHOTO_OFFSET) so the objective
 * ends up wherever the head is currently sighting.
 */

const LEICA_GREEN = "#00a651";
const LEICA_GREEN_DK = "#007a3d";
const KEY_BLACK = "#141518";
const CHROME = "#a2a6ac";
const LEICA_RED = "#c8102e";

// The reference photo has the objective on the LEFT (9 o'clock). We want
// the objective to point UP when hz=0 (i.e. North). So we rotate the
// bitmap by +90° on top of hz.
const PHOTO_OFFSET_DEG = 90;

// ---- Fixed compass rose behind the rotating instrument ------------------
const CompassRose = () => {
  const ticks = [];
  for (let i = 0; i < 72; i += 1) {
    ticks.push({ a: (i * 360) / 72, major: i % 9 === 0 });
  }
  return (
    <g>
      <circle cx={0} cy={0} r={150} fill="none" stroke="#1a3d2b" strokeWidth={1.5} />
      <circle cx={0} cy={0} r={158} fill="none" stroke="#0e2e1e" strokeWidth={2} />
      {ticks.map(({ a, major }, i) => (
        <line
          key={i}
          x1={0} y1={-150}
          x2={0} y2={major ? -138 : -144}
          stroke={major ? "#3ac170" : "#245a3d"}
          strokeWidth={major ? 1.5 : 0.8}
          transform={`rotate(${a})`}
        />
      ))}
      {["N", "E", "S", "W"].map((label, i) => {
        const a = (i * Math.PI) / 2;
        const r = 128;
        return (
          <text
            key={label}
            x={Math.sin(a) * r}
            y={-Math.cos(a) * r}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="monospace"
            fontWeight={label === "N" ? "bold" : "normal"}
            fontSize={label === "N" ? 16 : 12}
            fill={label === "N" ? "#00ff99" : "#4d7862"}
          >
            {label}
          </text>
        );
      })}
      {Array.from({ length: 12 }).map((_, i) => {
        const deg = i * 30;
        if (deg % 90 === 0) return null;
        const rad = (deg * Math.PI) / 180;
        return (
          <text
            key={deg}
            x={Math.sin(rad) * 128}
            y={-Math.cos(rad) * 128}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="monospace"
            fontSize={9}
            fill="#3d6a54"
          >
            {String(deg).padStart(3, "0")}
          </text>
        );
      })}
    </g>
  );
};

// ---- Zenith / V side-elevation gauge ------------------------------------
const TiltGauge = ({ v }) => {
  const deg = ((v * 180) / Math.PI);
  const needleDeg = Math.max(0, Math.min(360, deg));
  const R = 30;
  const halfCirc = Math.PI * R;
  const activeLen = (Math.min(180, needleDeg) / 180) * halfCirc;
  return (
    <div className="pointer-events-none absolute right-2 top-14 flex flex-col items-center">
      <div className="mb-0.5 text-[8px] font-mono uppercase tracking-widest text-[var(--text-dim)]">
        Zenith
      </div>
      <svg width={88} height={88} viewBox="-46 -46 92 92">
        <path d={`M 0 ${-R} A ${R} ${R} 0 0 1 0 ${R}`}
              fill="none" stroke="#1a3d2b" strokeWidth={5} strokeLinecap="round" />
        <path d={`M 0 ${-R} A ${R} ${R} 0 0 1 0 ${R}`}
              fill="none" stroke={LEICA_GREEN}
              strokeWidth={3.5} strokeLinecap="round"
              strokeDasharray={`${activeLen} 200`}
              opacity={0.9} />
        <line x1={0} y1={0} x2={R + 4} y2={0}
              stroke="#4d7862" strokeWidth={0.6} strokeDasharray="2 2" />
        {[0, 45, 90, 135, 180].map((t) => {
          const x1 = Math.sin((t * Math.PI) / 180) * (R - 4);
          const y1 = -Math.cos((t * Math.PI) / 180) * (R - 4);
          const x2 = Math.sin((t * Math.PI) / 180) * (R + 4);
          const y2 = -Math.cos((t * Math.PI) / 180) * (R + 4);
          return (
            <line key={t} x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="#3ac170" strokeWidth={t % 90 === 0 ? 1.2 : 0.6} />
          );
        })}
        <g transform={`rotate(${needleDeg})`}>
          <rect x={-1.5} y={-4} width={3} height={6} fill={CHROME} />
          <rect x={-2.4} y={-28} width={4.8} height={24}
                fill={KEY_BLACK} stroke={CHROME} strokeWidth={0.3} />
          <rect x={-4.2} y={-32} width={8.4} height={5}
                rx={1.2} fill={LEICA_GREEN} stroke={LEICA_GREEN_DK} strokeWidth={0.4} />
          <ellipse cx={0} cy={-31} rx={2.6} ry={0.8}
                   fill="#0a1220" stroke="#12386b" strokeWidth={0.3} />
        </g>
        <circle cx={0} cy={0} r={3} fill={LEICA_RED} stroke={KEY_BLACK} strokeWidth={0.6} />
        <text x={0} y={-R - 6} fontSize={7} textAnchor="middle"
              fill="#5d9c7b" fontFamily="monospace">0°</text>
        <text x={R + 8} y={0} fontSize={7} textAnchor="start" dominantBaseline="central"
              fill="#00ff99" fontFamily="monospace" fontWeight="bold">90°</text>
        <text x={0} y={R + 10} fontSize={7} textAnchor="middle"
              fill="#5d9c7b" fontFamily="monospace">180°</text>
      </svg>
      <div className="mt-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-mono text-[var(--cyan)]">
        {fmt(deg, 1)}°
      </div>
    </div>
  );
};

// ---- Wrapper panel -------------------------------------------------------
export const TS12Model = ({ state, connected }) => {
  const hz = state?.hz ?? 0;
  const v = state?.v ?? Math.PI / 2;
  const online = connected === undefined ? state?.connected : connected;

  const hzDeg = useMemo(() => radToDeg(hz), [hz]);
  const rotDeg = ((hz * 180) / Math.PI) + PHOTO_OFFSET_DEG;

  return (
    <div className="panel h-full min-h-[280px]" data-testid="ts12-3d-panel">
      <div className="panel-title flex items-center justify-between">
        <span>Leica TS12 · Plan View</span>
        <span className="text-[9px] tracking-widest text-[var(--text-dim)]">
          {online ? "LIVE" : "OFFLINE"}
        </span>
      </div>
      <div className="relative h-[calc(100%-2rem)] min-h-[240px] w-full overflow-hidden rounded-sm bg-[#050608]">
        {/* Grid background */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]"
             style={{
               backgroundImage:
                 "linear-gradient(to right, #00ff99 1px, transparent 1px), linear-gradient(to bottom, #00ff99 1px, transparent 1px)",
               backgroundSize: "24px 24px",
             }} />

        {/* Compass rose (fixed, world frame) */}
        <svg
          viewBox="-170 -170 340 340"
          className="absolute inset-0 h-full w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <radialGradient id="floorGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#0a2f1c" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#050608" stopOpacity={0} />
            </radialGradient>
          </defs>
          <circle cx={0} cy={0} r={160} fill="url(#floorGlow)" />
          <CompassRose />
          {/* Highlighted current-bearing line */}
          <g transform={`rotate(${(hz * 180) / Math.PI})`}>
            <line x1={0} y1={-158} x2={0} y2={-135}
                  stroke="#00ff99" strokeWidth={1.5} opacity={0.75} />
          </g>
          {/* Red aim laser — shoots straight out of the objective in the
              current heading direction. Drawn as a bright core line with a
              soft outer glow so it reads as a beam even from the corner
              of the eye. Rotates with hz just like the instrument photo. */}
          <defs>
            <linearGradient id="laserBeam" x1="0" y1="0" x2="0" y2="-1">
              <stop offset="0%"  stopColor="#ff2233" stopOpacity="1" />
              <stop offset="55%" stopColor="#ff5566" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ff5566" stopOpacity="0" />
            </linearGradient>
            <filter id="laserGlow" x="-200%" y="-200%" width="400%" height="400%">
              <feGaussianBlur stdDeviation="2.2" result="b1" />
              <feMerge>
                <feMergeNode in="b1" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g transform={`rotate(${(hz * 180) / Math.PI})`} data-testid="ts12-aim-laser">
            {/* Soft outer bloom */}
            <line x1={0} y1={-40} x2={0} y2={-165}
                  stroke="#ff2233" strokeWidth={5} strokeLinecap="round"
                  opacity={0.28} filter="url(#laserGlow)" />
            {/* Bright inner core */}
            <line x1={0} y1={-40} x2={0} y2={-165}
                  stroke="url(#laserBeam)" strokeWidth={1.6} strokeLinecap="round" />
            {/* Muzzle flare at the objective end */}
            <circle cx={0} cy={-46} r={4} fill="#ff2233" opacity={0.85}
                    filter="url(#laserGlow)" />
            <circle cx={0} cy={-46} r={1.6} fill="#ffe1e5" opacity={0.95}>
              <animate attributeName="opacity" values="0.6;1;0.6" dur="1.4s" repeatCount="indefinite" />
            </circle>
            {/* Arrowhead tip at the far end */}
            <polygon points="0,-165 -3,-158 3,-158" fill="#ff2233" opacity={0.85} />
          </g>
        </svg>

        {/* The actual Leica TS12 top-down photo, rotated with hz */}
        <img
          src="/ts12-plan.png"
          alt="Leica TS12 plan view"
          data-testid="ts12-photo"
          className="pointer-events-none absolute left-1/2 top-1/2 max-h-[68%] max-w-[68%] select-none"
          style={{
            transform: `translate(-50%, -50%) rotate(${rotDeg}deg)`,
            transition: "transform 220ms ease-out",
            filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.55))",
          }}
        />

        {/* HUD readouts */}
        <div className="pointer-events-none absolute left-2 top-2 flex flex-col gap-0.5 text-[10px] font-mono">
          <span className="rounded bg-black/60 px-1.5 py-0.5 text-[var(--neon)]">
            Hz {formatDMS(hz)}
          </span>
          <span className="rounded bg-black/60 px-1.5 py-0.5 text-[var(--text-dim)]">
            {fmt(hzDeg, 1)}°
          </span>
        </div>

        {/* Zenith gauge */}
        <TiltGauge v={v} />
      </div>
    </div>
  );
};

export default TS12Model;
